// ============================================================================
// Reservation Data Context Types
// Types for the shared ReservationDataContext service with viewport-based loading
// ============================================================================

import { Reservation, MapSection } from './index';

// ============================================================================
// Viewport Configuration
// ============================================================================

export interface ViewportConfig {
  visibleSiteCount: number; // Default ~30 sites visible vertically
  visibleDays: number; // Default ~14 days visible horizontally
  prefetchBuffer: number; // Extra chunks to prefetch (default 1)
}

// ============================================================================
// Chunk Types for Viewport-Based Loading
// ============================================================================

/**
 * A chunk represents a fetched date range.
 * Since the API doesn't support site_ids filter, we fetch by date range
 * and filter client-side by visible sites.
 */
export interface DateChunk {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

/**
 * Key format: "dates:YYYY-MM-DD:YYYY-MM-DD"
 */
export type ChunkKey = string;

// ============================================================================
// Viewport State
// ============================================================================

export interface ViewportState {
  // Current visible range
  startSiteIndex: number;
  endSiteIndex: number;
  startDateIndex: number;
  endDateIndex: number;

  // Scroll position tracking
  lastScrollLeft: number;
  lastScrollTop: number;
}

// ============================================================================
// Grid Site (for display in schedule grid)
// ============================================================================

export interface GridSite {
  id: number;
  alias: string;
  name: string;
  siteType: string;
  section: string;
  maxLength?: number;
  seasonal?: boolean;
}

// ============================================================================
// Main State Interface
// ============================================================================

export interface ReservationDataState {
  // Sites for grid (derived from SiteDataContext)
  sites: GridSite[];
  sections: MapSection[];
  sitesLoading: boolean;
  sitesLoaded: boolean;

  // Reservations (loaded by viewport)
  reservations: Reservation[];
  reservationsLoading: boolean;
  reservationsError: string | null;

  // Chunk tracking
  loadedChunks: Set<ChunkKey>;
  loadingChunks: Set<ChunkKey>;

  // Viewport state
  viewport: ViewportState;

  // Season configuration
  selectedYear: number;
  seasonStartDate: string;
  seasonEndDate: string;
  allDates: string[]; // Full season date array

  // Search filters
  searchCode: string;
  searchEmail: string;
  searchName: string;
}

// ============================================================================
// Reducer Actions
// ============================================================================

export type ReservationDataAction =
  // Sites actions (from SiteDataContext)
  | { type: 'SITES_LOADING' }
  | { type: 'SITES_LOADED'; payload: { sites: GridSite[]; sections: MapSection[] } }

  // Reservations chunk actions
  | { type: 'CHUNK_LOADING'; payload: ChunkKey }
  | { type: 'CHUNK_LOADED'; payload: { chunkKey: ChunkKey; reservations: Reservation[] } }
  | { type: 'CHUNK_ERROR'; payload: { chunkKey: ChunkKey; error: string } }

  // Bulk reservation update (for full refresh)
  | { type: 'RESERVATIONS_LOADING' }
  | { type: 'RESERVATIONS_LOADED'; payload: Reservation[] }
  | { type: 'RESERVATIONS_ERROR'; payload: string }

  // Viewport actions
  | { type: 'UPDATE_VIEWPORT'; payload: Partial<ViewportState> }

  // Year/season actions
  | { type: 'SET_YEAR'; payload: number }

  // Search actions
  | { type: 'SET_SEARCH'; payload: { searchCode?: string; searchEmail?: string; searchName?: string } }
  | { type: 'CLEAR_SEARCH' }

  // Cache invalidation
  | { type: 'INVALIDATE_RESERVATIONS' }
  | { type: 'INVALIDATE_CHUNKS' }

  // Reservation CRUD notifications
  | { type: 'RESERVATION_CREATED'; payload: Reservation }
  | { type: 'RESERVATION_UPDATED'; payload: number }
  | { type: 'RESERVATION_CANCELLED'; payload: number };

// ============================================================================
// Context Value Interface
// ============================================================================

export interface ReservationDataContextValue {
  // State (read-only)
  state: ReservationDataState;

  // Computed values
  visibleReservations: Reservation[]; // Filtered by viewport + search
  visibleSites: GridSite[]; // Sites in current viewport
  visibleDates: string[]; // Dates in current viewport
  hasSearch: boolean;
  firstMatchId?: number; // For search highlighting

  // Viewport-based loading
  loadViewportChunks: () => Promise<void>;
  onScroll: (scrollLeft: number, scrollTop: number) => void;

  // Data actions
  loadSites: () => Promise<void>;
  refreshReservations: () => Promise<void>; // Force reload all

  // Year/season control
  setYear: (year: number) => void;

  // Search actions
  setSearch: (search: { searchCode?: string; searchEmail?: string; searchName?: string }) => void;
  clearSearch: () => void;

  // Cache management
  invalidateReservations: () => void;

  // CRUD notifications
  onReservationCreated: (reservation: Reservation) => void;
  onReservationUpdated: (reservationId: number) => void;
  onReservationCancelled: (reservationId: number) => void;

  // Utility
  getChunkKey: (startDate: string, endDate: string) => ChunkKey;
  isChunkLoaded: (chunkKey: ChunkKey) => boolean;
  isChunkLoading: (chunkKey: ChunkKey) => boolean;
}

// ============================================================================
// Helper Functions for Chunk Keys
// ============================================================================

export function createChunkKey(startDate: string, endDate: string): ChunkKey {
  return `dates:${startDate}:${endDate}`;
}

export function parseChunkKey(key: ChunkKey): DateChunk | null {
  const match = key.match(/^dates:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/);
  if (!match) return null;
  return {
    startDate: match[1],
    endDate: match[2],
  };
}
